import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Formik, Form, FieldArray } from 'formik';
import holdingsLogo from '../assets/images/holdings.svg';
import Input from '../components/input';
import Select from '../components/Select';
import { apiService } from '../services/apiService';
import SpinnerLoader from '../utils/SpinnerLoader';
import '../styles/Issue.css';
import '../styles/RFQRequest.css';
import { errorNotify, successNotify } from '../utils/toast';
import { RFQRequestSchema } from '../utils/Schema';

const deliveryOptions = [
    { label: 'Delivery', value: 'Delivery' },
    { label: 'Pickup', value: 'Pickup' }
];

const DEFAULT_INFO_STATE = {
    already_bid: false,
    bid_info: null,
    status: true,
    messaging: [],
    vendor: null,
    rfqId: null,
    rfqRefId: null,
};

const hasQueryValue = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== '';

const getRawQueryValue = (search, key) => {
    if (!search || !key) return null;
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = search.match(new RegExp(`[?&]${escapedKey}=([^&#]*)`));
    if (!match || match[1] === undefined) return null;

    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
};

const pickQueryParam = ({
    keys,
    searchParams,
    fallbackSearchParams,
    rawSearch,
    preservePlus = false,
}) => {
    for (const key of keys) {
        if (preservePlus) {
            const rawValue = getRawQueryValue(rawSearch, key);
            if (hasQueryValue(rawValue)) return rawValue;
        }

        const value = searchParams.get(key) || fallbackSearchParams.get(key);
        if (hasQueryValue(value)) return value;
    }

    return null;
};

const formatNumber = (value, fallback = '0') => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return fallback;
    return numericValue.toLocaleString();
};

const formatPercent = (value, fallback = '0') => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return `${fallback}%`;
    return `${Number(numericValue.toFixed(2)).toLocaleString()}%`;
};

const resolveNumericId = (...values) => {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            return value;
        }

        if (typeof value !== 'string') continue;

        const trimmedValue = value.trim();
        if (!trimmedValue) continue;

        const directValue = Number(trimmedValue);
        if (Number.isFinite(directValue) && directValue > 0) {
            return directValue;
        }

        const digitMatches = trimmedValue.match(/\d+/g);
        if (!digitMatches?.length) continue;

        const parsedValue = Number(digitMatches[digitMatches.length - 1]);
        if (Number.isFinite(parsedValue) && parsedValue > 0) {
            return parsedValue;
        }
    }

    return null;
};

const toSafeNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

const toSafePercentage = (value) => Math.min(100, Math.max(0, toSafeNumber(value)));

const roundToTwo = (value) => Number(toSafeNumber(value).toFixed(2));
const RFQ_BID_MAX_AMOUNT = 99999999.99;
const RFQ_BID_AMOUNT_ERROR = 'The amount is too large. Enter a valid amount.';
const NUMERIC_OVERFLOW_REGEX = /(numeric field overflow|value out of range|out of range for type numeric|invalid input syntax for type numeric)/i;

const isValidBidAmount = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue >= 0 && numericValue <= RFQ_BID_MAX_AMOUNT;
};

const normalizeBidAmount = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0 || numericValue > RFQ_BID_MAX_AMOUNT) {
        return { value: 0, valid: false };
    }
    return { value: numericValue, valid: true };
};

const calculateQuoteSummary = (values) => {
    const items = Array.isArray(values?.items) ? values.items : [];
    const discountPercent = toSafePercentage(values?.discount);
    const shippingRawValue = values?.shipping;
    const shippingInput = String(shippingRawValue ?? '').trim();
    const shippingParsed = normalizeBidAmount(shippingRawValue);
    const shippingAmount = shippingParsed.value;
    const taxPercent = toSafePercentage(values?.tax);
    let hasOverflow = !shippingParsed.valid && shippingInput !== '';

    const subTotal = items.reduce((sum, item) => {
        const quantity = Math.max(0, toSafeNumber(item?.quantity));
        const unitPriceRawValue = item?.unitPrice;
        const unitPriceInput = String(unitPriceRawValue ?? '').trim();
        const unitPriceParsed = normalizeBidAmount(unitPriceRawValue);

        if (!unitPriceParsed.valid && unitPriceInput !== '') {
            hasOverflow = true;
            return sum;
        }

        const lineTotal = quantity * unitPriceParsed.value;
        const nextSubTotal = sum + lineTotal;

        if (!isValidBidAmount(lineTotal) || !isValidBidAmount(nextSubTotal)) {
            hasOverflow = true;
            return sum;
        }

        return nextSubTotal;
    }, 0);

    const discountAmount = subTotal * (discountPercent / 100);
    const taxAmount = subTotal * (taxPercent / 100);
    const grandTotalRaw = subTotal - discountAmount + shippingAmount + taxAmount;
    const grandTotal = isValidBidAmount(grandTotalRaw) ? grandTotalRaw : 0;

    if (!isValidBidAmount(grandTotalRaw)) {
        hasOverflow = true;
    }

    return {
        subTotal: roundToTwo(subTotal),
        discountPercent: roundToTwo(discountPercent),
        discountAmount: roundToTwo(discountAmount),
        shippingAmount: roundToTwo(shippingAmount),
        taxPercent: roundToTwo(taxPercent),
        taxAmount: roundToTwo(taxAmount),
        grandTotal: roundToTwo(grandTotal),
        hasOverflow,
    };
};

const RFQRequest = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const rawSearch = window.location.search || '';

    const fallbackSearchParams = useMemo(
        () => new URLSearchParams(rawSearch),
        [rawSearch]
    );

    const parts_rfq_id = pickQueryParam({
        keys: ['parts_rfq_id', 'rfq_id'],
        searchParams,
        fallbackSearchParams,
        rawSearch,
    });
    const poc_contact = pickQueryParam({
        keys: ['poc_contact', 'contact_number'],
        searchParams,
        fallbackSearchParams,
        rawSearch,
        preservePlus: true,
    });
    const normalizedPocContact = typeof poc_contact === 'string' ? poc_contact.trim() : poc_contact;

    const [loading, setLoading] = useState(true);
    const [rfqData, setRfqData] = useState(null);
    const [info, setInfo] = useState(DEFAULT_INFO_STATE);

    const fetchRFQ = useCallback(async () => {
        if (!parts_rfq_id || !normalizedPocContact) {
            setInfo((previous) => ({
                ...previous,
                status: false,
                messaging: [`Missing required parameters. Received ID: ${parts_rfq_id}, Contact: ${normalizedPocContact}`],
            }));
            setRfqData(null);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await apiService.getRFQByVendor({ parts_rfq_id, poc_contact: normalizedPocContact });
            if (response?.statusCode === 200) {
                const rfqRecord = response?.data?.data || null;

                setRfqData(rfqRecord);
                setInfo({
                    already_bid: Boolean(response?.data?.already_bid),
                    bid_info: response?.data?.bid_info || null,
                    status: response?.data?.status !== false,
                    messaging: response?.data?.messaging || [],
                    vendor: response?.data?.vendor || null,
                    rfqId: resolveNumericId(
                        rfqRecord?.id,
                        response?.data?.rfq_id,
                        response?.data?.parts_rfq_id,
                        rfqRecord?.parts_rfq_id,
                        parts_rfq_id
                    ),
                    rfqRefId: rfqRecord?.parts_rfq_id || response?.data?.parts_rfq_id || parts_rfq_id || 0,
                });
            } else {
                setRfqData(null);
                setInfo((previous) => ({
                    ...previous,
                    status: false,
                    messaging: ['Unable to fetch RFQ details right now.'],
                }));
            }
        } catch (error) {
            console.error("Error fetching RFQ:", error);
            errorNotify(error || 'Failed to load RFQ details');
            setRfqData(null);
            setInfo((previous) => ({
                ...previous,
                status: false,
                messaging: ['Failed to load RFQ details. Please try again.'],
            }));
        } finally {
            setLoading(false);
        }
    }, [parts_rfq_id, normalizedPocContact]);

    useEffect(() => {
        fetchRFQ();
    }, [fetchRFQ]);

    const initialValues = useMemo(() => ({
        deliveryType: '',
        deliveryDate: '',
        discount: '',
        shipping: '',
        tax: '',
        notes: '',
        items: rfqData?.items?.map((item) => ({
            id: item.id,
            rfq_item_id: item.part_rfq_item_id || `RFQI-${item.id}`,
            part_id: item.part_id,
            name: item.part?.name || 'Unknown Part',
            quantity: item.quantity,
            unitPrice: '',
        })) || []
    }), [rfqData]);

    const resolvedPartsRfqId = useMemo(() => (
        resolveNumericId(
            rfqData?.id,
            info?.rfqId,
            rfqData?.parts_rfq_id,
            info?.rfqRefId,
            parts_rfq_id
        )
    ), [info?.rfqId, info?.rfqRefId, parts_rfq_id, rfqData]);
    const displayRfqRefId = resolvedPartsRfqId || info?.rfqId || '--';

    const handleSubmit = useCallback(async (values, { setSubmitting, resetForm }) => {
        try {
            const quoteSummary = calculateQuoteSummary(values);
            const discount = quoteSummary.discountPercent;
            const shipping = quoteSummary.shippingAmount;
            const tax = quoteSummary.taxPercent;
            const subTotal = quoteSummary.subTotal;
            const totalAmount = quoteSummary.grandTotal;

            if (quoteSummary.hasOverflow) {
                errorNotify(RFQ_BID_AMOUNT_ERROR);
                return;
            }

            if (!resolvedPartsRfqId) {
                errorNotify("Unable to determine RFQ ID for this quotation.");
                return;
            }

            const payload = {
                parts_rfq_id: resolvedPartsRfqId,
                contact_number: normalizedPocContact,
                delivery_type: values.deliveryType,
                delivery_date: values.deliveryDate,
                sub_total: subTotal,
                discount,
                shipping,
                total_amount: totalAmount,
                tax,
                items: values.items.map((item) => ({
                    part_id: toSafeNumber(item.part_id),
                    quantity: toSafeNumber(item.quantity),
                    unit_price: toSafeNumber(item.unitPrice)
                }))
            };
            await apiService.submitVendorBid(payload);
            successNotify("Quote submitted successfully");
            await fetchRFQ();
            resetForm();
        } catch (error) {
            const rawError =
                typeof error === 'string'
                    ? error
                    : Array.isArray(error)
                        ? error[0]
                        : (error?.message || String(error || ''));

            if (NUMERIC_OVERFLOW_REGEX.test(rawError)) {
                errorNotify(RFQ_BID_AMOUNT_ERROR);
            } else {
                errorNotify(rawError || "Failed to submit quote");
            }
        } finally {
            setSubmitting(false);
        }
    }, [fetchRFQ, normalizedPocContact, resolvedPartsRfqId]);

    const PageWrapper = ({ children, title }) => (
        <div className="rfq-request-bg" style={{ backgroundColor: '#f4f7fe', minHeight: '100vh', padding: '20px 0' }}>
            <div className="rfq-request-page" style={{ maxWidth: '767px', margin: '0 auto', padding: '0 15px' }}>
                <div className="card shadow-sm border-0 mb-4 rfq-request-card" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                    <div className="card-header bg-white py-3 px-4 border-bottom-0 rfq-request-card-header">
                        <h5 className="mb-0" style={{ fontWeight: '700', color: '#333' }}>
                            {title || 'Request for Quotation'}
                        </h5>
                    </div>
                    <div className="card-body p-4 p-md-5 pt-0 rfq-request-card-body">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );

    const hasMessages = (info?.messaging || []).length > 0;
    const isAccessDenied = hasMessages && info?.status === false;
    const shouldShowForm = info?.already_bid === false && info?.status === true && !hasMessages;
    const shouldShowSummary = info?.already_bid === true && info?.status === true && !hasMessages;
    const bidInfo = info?.bid_info || {};
    const summarySubTotal = Math.max(0, toSafeNumber(bidInfo?.sub_total));
    const summaryDiscountPercent = Math.max(0, toSafeNumber(bidInfo?.total_discount));
    const summaryShippingAmount = Math.max(0, toSafeNumber(bidInfo?.total_shipping));
    const summaryTaxPercent = Math.max(0, toSafeNumber(bidInfo?.total_tax));
    const summaryTotalAmount = Math.max(0, toSafeNumber(bidInfo?.total_amount));
    const summaryDiscountAmount = roundToTwo(summarySubTotal * (summaryDiscountPercent / 100));
    const summaryTaxAmount = roundToTwo(summarySubTotal * (summaryTaxPercent / 100));

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', backgroundColor: '#f4f7fe' }}>
                <SpinnerLoader />
            </div>
        );
    }

    if (isAccessDenied) {
        return (
            <PageWrapper title="Request for Quotation">
                <div className="text-center py-5">
                    <h4 className="text-danger mb-3">Access Denied</h4>
                    <p className="text-muted">{info?.messaging?.[0]}</p>
                </div>
            </PageWrapper>
        );
    }

    return (
        <>
         {shouldShowForm &&(
          <PageWrapper title={<span>Request for Quotation <span style={{ color: '#0d6efd' }}># RFQ-{displayRfqRefId}</span></span>}>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={RFQRequestSchema}
                validateOnMount
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched, isSubmitting, submitCount }) => {
                    const quoteSummary = calculateQuoteSummary(values);
                    const handlePercentageFieldChange = (fieldName) => (event) => {
                        const rawValue = event.target.value;

                        if (rawValue === '') {
                            setFieldValue(fieldName, '');
                            return;
                        }

                        const parsedValue = Number(rawValue);
                        if (!Number.isFinite(parsedValue)) return;

                        const clampedValue = Math.min(100, Math.max(0, parsedValue));
                        setFieldValue(fieldName, clampedValue);
                    };
                    return (
                    <Form>
                        {/* Header / Info Section */}
                        <div className="d-flex justify-content-between align-items-start mb-4 rfq-top-info">
                            <div>
                                <h5 style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#333', marginBottom: '10px' }}>
                                    <span style={{ color: '#0d6efd' }}>Hello</span> {info?.vendor?.name}
                                </h5>
                                <p className="text-muted mb-1" style={{ fontSize: '0.9rem' }}>This is a new RFQ from J Holding Private Limited (JHPL)</p>
                                <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Email: enquiries@jholdings.com.pk</p>
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>Phone: +92 21 111 252 575</p>
                            </div>
                            <div>
                                <div style={{ padding: '0px' }}>
                                    <img
                                        src={holdingsLogo}
                                        alt="J HOLDINGS"
                                        className="rfq-top-logo"
                                        style={{ height: '70px', objectFit: 'contain' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Target Delivery Date Banner */}
                        <div className="mb-3 px-3 py-2 rfq-target-banner" style={{ backgroundColor: '#f9f9fb', borderRadius: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: '500', color: '#000', fontSize: '0.9rem' }}>Target Delivery Date</span>
                            <span style={{ fontWeight: '500', color: '#555', fontSize: '0.9rem' }}>
                                <i className="bi bi-calendar-event me-2"></i>
                                {rfqData?.target_delivery_date ? new Date(rfqData.target_delivery_date).toISOString().split('T')[0] : 'N/A'}
                            </span>
                        </div>

                        {/* Form Inputs */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Select
                                    label={<span>Delivery Type <span className="text-danger">*</span> <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.8rem' }}></i></span>}
                                    placeholder="Select Delivery Type"
                                    options={deliveryOptions}
                                    value={deliveryOptions.find(o => o.value === values.deliveryType)}
                                    onChange={(val) => setFieldValue('deliveryType', val?.value)}
                                    onBlur={() => setFieldTouched('deliveryType', true)}
                                    error={errors.deliveryType}
                                    touched={touched.deliveryType || submitCount > 0}
                                />
                            </Col>
                            <Col md={6}>
                                <Input
                                    label={<span>Delivery Date <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.8rem' }}></i></span>}
                                    type="date"
                                    name="deliveryDate"
                                    placeholder="YYYY"
                                    value={values.deliveryDate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.deliveryDate}
                                    touched={touched.deliveryDate || submitCount > 0}
                                    className="rfq-delivery-date-field"
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Input
                                    label={<>Discount (%)</>}
                                    type="number"
                                    name="discount"
                                    placeholder="0"
                                    value={values.discount}
                                    onChange={handlePercentageFieldChange('discount')}
                                    onBlur={handleBlur}
                                    error={errors.discount}
                                    touched={touched.discount}
                                    required={false}
                                />
                            </Col>
                            <Col md={4}>
                                <Input
                                    label={<>Shipping (PKR)</>}
                                    type="number"
                                    name="shipping"
                                    placeholder="0"
                                    value={values.shipping}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.shipping}
                                    touched={touched.shipping}
                                    required={false}
                                />
                            </Col>
                            <Col md={4}>
                                <Input
                                    label={<>Tax (%)</>}
                                    type="number"
                                    name="tax"
                                    placeholder="0"
                                    value={values.tax}
                                    onChange={handlePercentageFieldChange('tax')}
                                    onBlur={handleBlur}
                                    error={errors.tax}
                                    touched={touched.tax}
                                    required={false}
                                />
                            </Col>
                        </Row>

                        {/* Items Table */}
                        <FieldArray name="items">
                            {() => (
                                <div className="mb-4" style={{ marginBottom: '2rem' }}>
                                    <style>
                                        {`
                                            .custom-scroll::-webkit-scrollbar {
                                                width: 6px;
                                            }
                                            .custom-scroll::-webkit-scrollbar-track {
                                                background: #f1f1f1;
                                                border-radius: 4px;
                                            }
                                            .custom-scroll::-webkit-scrollbar-thumb {
                                                background: #0d6efd;
                                                border-radius: 4px;
                                            }
                                            .custom-scroll::-webkit-scrollbar-thumb:hover {
                                                background: #0b5ed7;
                                            }
                                            
                                            /* Table Design Overrides */
                                            .custom-rfq-table {
                                                border-collapse: separate; 
                                                border-spacing: 0;
                                            }
                                            .custom-rfq-table thead th {
                                                background-color: #fff !important;
                                                color: #888ea8 !important;
                                                border-bottom: 1px solid #e0e6ed !important;
                                                border-top: none !important;
                                                border-left: none !important;
                                                border-right: none !important;
                                                font-weight: 400 !important;
                                                font-size: 13px !important;
                                            }
                                            .custom-rfq-table tbody td {
                                                border-top: none !important;
                                                border-bottom: 1px solid #f9f9f9 !important;
                                                border-left: none !important;
                                                border-right: none !important;
                                                color: #3b3f5c !important;
                                                vertical-align: middle !important;
                                            }
                                            .custom-rfq-table tbody tr:last-child td {
                                                border-bottom: none !important;
                                            }
                                        `}
                                    </style>
                                    <div className="table-responsive custom-scroll" style={{ border: '1px solid #e0e6ed', borderRadius: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                                        <table className="table mb-0 custom-rfq-table">
                                            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                <tr>
                                                    <th className="ps-4 py-3">Part ID</th>
                                                    <th className="py-3">Part Name</th>
                                                    <th className="py-3">Quantity</th>
                                                    <th className="py-3 pe-4" style={{ width: '180px' }}>Unit Price (PKR)</th>
                                                    <th className="py-3 pe-4 text-end" style={{ width: '180px' }}>Line Total (PKR)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {values.items.map((item, index) => {
                                                    const rawLineTotal = toSafeNumber(item.quantity) * toSafeNumber(item.unitPrice);
                                                    const lineTotal = isValidBidAmount(rawLineTotal) ? roundToTwo(rawLineTotal) : 0;
                                                    const unitPriceError = errors.items?.[index]?.unitPrice;
                                                    const touchedUnitPrice = touched.items?.[index]?.unitPrice;
                                                    const lineTotalOverflow = !isValidBidAmount(rawLineTotal) && String(item?.unitPrice ?? '').trim() !== '';
                                                    const shouldShowUnitPriceError = Boolean((touchedUnitPrice || submitCount > 0) && unitPriceError);
                                                    const shouldShowOverflowError = Boolean(lineTotalOverflow && !shouldShowUnitPriceError);
                                                    const showInputErrorState = shouldShowUnitPriceError || shouldShowOverflowError;
                                                    return (
                                                    <tr key={item.id}>
                                                        <td className="ps-4" style={{ fontWeight: '500' }}>
                                                            <span style={{ color: '#4361ee', textDecoration: 'underline', cursor: 'pointer' }}>
                                                                {item.rfq_item_id}
                                                            </span>
                                                        </td>
                                                        <td>{item.name}</td>
                                                        <td>{item.quantity}</td>
                                                        <td className="pe-4">
                                                            <input
                                                                type="number"
                                                                name={`items[${index}].unitPrice`}
                                                                value={item.unitPrice}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                className={`form-control ${showInputErrorState ? 'is-invalid' : ''}`}
                                                                min="0"
                                                                max={RFQ_BID_MAX_AMOUNT}
                                                                step="0.01"
                                                                placeholder=""
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    fontSize: '0.9rem',
                                                                    height: '36px',
                                                                    borderColor: showInputErrorState ? '#dc3545' : '#e0e6ed',
                                                                    borderRadius: '6px',
                                                                    boxShadow: 'none'
                                                                }}
                                                                title={shouldShowUnitPriceError ? unitPriceError : (shouldShowOverflowError ? RFQ_BID_AMOUNT_ERROR : '')}
                                                            />
                                                            {shouldShowUnitPriceError && (
                                                                <div className="invalid-feedback d-block">{unitPriceError}</div>
                                                            )}
                                                            {shouldShowOverflowError && (
                                                                <div className="invalid-feedback d-block">{RFQ_BID_AMOUNT_ERROR}</div>
                                                            )}
                                                         </td>
                                                        <td className="pe-4 text-end" style={{ fontWeight: 600 }}>
                                                            {formatNumber(lineTotal)}
                                                        </td>
                                                    </tr>
                                                )})}
                                                {values.items.length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-3 text-muted">No items found in this RFQ.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </FieldArray>

                        <div className="card mb-4 rfq-cost-summary-card" style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '20px' }}>
                            <h6 style={{ fontWeight: 'bold', fontSize: '1.05rem', marginBottom: '1rem', color: '#333' }}>Cost Summary</h6>
                            <div className="d-flex justify-content-between mb-2">
                                <span style={{ color: '#6b7280' }}>Subtotal</span>
                                <span style={{ fontWeight: 600 }}>PKR {formatNumber(quoteSummary.subTotal)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span style={{ color: '#6b7280' }}>Discount ({formatNumber(quoteSummary.discountPercent)}%)</span>
                                <span style={{ fontWeight: 600, color: '#dc3545' }}>- PKR {formatNumber(quoteSummary.discountAmount)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span style={{ color: '#6b7280' }}>Shipping (PKR)</span>
                                <span style={{ fontWeight: 600 }}>+ PKR {formatNumber(quoteSummary.shippingAmount)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span style={{ color: '#6b7280' }}>Tax ({formatNumber(quoteSummary.taxPercent)}%)</span>
                                <span style={{ fontWeight: 600 }}>+ PKR {formatNumber(quoteSummary.taxAmount)}</span>
                            </div>
                            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '10px', paddingTop: '10px' }} className="d-flex justify-content-between">
                                <span style={{ fontWeight: 700, color: '#111827' }}>Grand Total</span>
                                <span style={{ fontWeight: 800, color: '#1447B1' }}>PKR {formatNumber(quoteSummary.grandTotal)}</span>
                            </div>
                            {quoteSummary.hasOverflow && (
                                <div className="text-danger mt-2" style={{ fontSize: '0.85rem' }}>
                                    {RFQ_BID_AMOUNT_ERROR}
                                </div>
                            )}
                        </div>

                        {/* General Notes Card */}
                        <div className="card mb-4 rfq-general-notes" style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '20px' }}>
                            <h6 style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.5rem', color: '#333' }}>General Notes</h6>
                            <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Add any additional notes or observations here.</p>
                            <div>
                                <textarea
                                    className="form-control"
                                    rows={5}
                                    name="notes"
                                    placeholder="The supplier must ensure valid transporter insurance and up-to-date vehicle documents before dispatch. All required transport and goods-in-transit insurance documents must be provided with each shipment."
                                    value={values.notes}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    style={{ backgroundColor: '#f9f9fb', fontSize: '0.85rem', resize: 'none', color: '#555', padding: '15px', borderRadius: '6px', border: '1px solid #e9ecef' }}
                                />
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="d-flex gap-3 justify-content-end rfq-form-actions">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="rfq-btn-secondary"
                                style={{
                                    border: '1px solid #1447B1',
                                    color: '#1447B1',
                                    backgroundColor: '#fff',
                                    padding: '8px 30px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s',
                                    height: '42px',
                                    minWidth: '100px'
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={isSubmitting || quoteSummary.hasOverflow}
                                className="rfq-btn-primary"
                                style={{
                                    backgroundColor: '#1447B1',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 24px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    flex: 1,
                                    maxWidth: '80%',
                                    height: '42px',
                                    boxShadow: '0 2px 4px rgba(20, 71, 177, 0.2)',
                                    opacity: isSubmitting ? 0.7 : 1,
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </Form>
                )}}
            </Formik>
        </PageWrapper>
         )}

        {shouldShowSummary && (
            <PageWrapper title={<span>Quotation Details <span style={{ color: '#0d6efd' }}># RFQ-{displayRfqRefId}</span></span>}>
                <div className="text-center mb-5 mt-4 rfq-thankyou-hero">
                    <div className="mb-4 rfq-thankyou-icon-wrap" style={{ position: 'relative', display: 'inline-block' }}>
                        <div className="rfq-thankyou-icon-main" style={{ backgroundColor: '#ecfdf5', width: '90px', height: '90px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                            <i className="bi bi-check2-circle" style={{ fontSize: '48px', color: '#10b981' }}></i>
                        </div>
                        <div className="rfq-thankyou-icon-badge-shell" style={{ position: 'absolute', bottom: '0', right: '0', backgroundColor: '#fff', borderRadius: '50%', padding: '2px' }}>
                            <div className="rfq-thankyou-icon-badge" style={{ backgroundColor: '#10b981', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="bi bi-check-lg" style={{ color: '#fff', fontSize: '14px' }}></i>
                            </div>
                        </div>
                    </div>
                    <h2 className="rfq-thankyou-title" style={{ fontWeight: '800', color: '#111827', fontSize: '1.75rem', marginBottom: '0.75rem' }}>
                        Thank You, {info?.vendor?.name || 'Supplier'}!
                    </h2>
                    <p className="text-muted mx-auto rfq-thankyou-desc" style={{ maxWidth: '400px', fontSize: '0.95rem', lineHeight: '1.6' }}>
                        Your quotation has been successfully received and sent to our procurement team for review.
                    </p>
                </div>

                   {/* Quoted Items Section */}
                <div className="mb-4 d-flex align-items-center justify-content-between rfq-line-items-header">
                    <h6 className="mb-0" style={{ fontWeight: '700', color: '#111827' }}>Line Items List</h6>
                    <span className="badge rounded-pill bg-light text-dark border" style={{ fontSize: '0.75rem', padding: '6px 12px' }}>
                        {bidInfo?.items?.length || 0} Items
                    </span>
                </div>

                <div className="table-responsive rfq-thankyou-table-wrap" style={{ margin: '20px 0', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <table className="table mb-0 align-middle rfq-thankyou-table">
                        <thead style={{ backgroundColor: '#f9fafb' }}>
                            <tr>
                                <th className="ps-4 py-3 border-0 text-center" style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', width: '80px' }}>S.No #</th>
                                <th className="ps-4 py-3 border-0" style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</th>
                                <th className="py-3 text-center border-0" style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', width: '100px' }}>Quantity</th>
                                <th className="py-3 text-end pe-4 border-0" style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', width: '150px' }}>Unit Price</th>
                            </tr>
                        </thead>
                        <tbody style={{ borderTop: 'none', marginBottom: "20px" }}>
                            {bidInfo?.items?.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: idx === bidInfo.items.length - 1 ? 'none' : '1px solid #f3f4f6' }}>
                                    <td className="ps-4 py-3 text-center" data-label="S.No #">
                                        <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>{idx + 1}</span>
                                    </td>
                                    <td className="ps-4 py-3" data-label="Description">
                                        <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem', marginBottom: '2px' }}>
                                            {item.part?.name || item.part?.description || 'Part Details'}
                                        </div>
                                        <div style={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                            {item.part?.part_number || 'No SKU'}
                                        </div>
                                    </td>
                                    <td className="py-3 text-center" data-label="Quantity">
                                        <span style={{ fontWeight: '500', color: '#374151', fontSize: '0.9rem' }}>{item.quantity}</span>
                                    </td>
                                    <td className="py-3 text-end pe-4" data-label="Unit Price">
                                        <div style={{ fontWeight: '700', color: '#111827', fontSize: '0.9rem' }}>
                                            {formatNumber(item.unit_price)}
                                        </div>
                                        <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>PKR / Unit</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {/* <tfoot style={{ backgroundColor: '#f9fafb' }}>
                            <tr>
                                <td colSpan="3" className="ps-4 py-3 border-0 text-end" data-label="" style={{ fontWeight: '600', color: '#4b5563', fontSize: '0.9rem' }}>Grand Total Amount:</td>
                                <td className="py-3 text-end pe-4 border-0" data-label="" style={{ fontWeight: '800', color: '#1447B1', fontSize: '1.05rem' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: '600', marginRight: '4px' }}>PKR</span>
                                    {formatNumber(summaryTotalAmount)}
                                </td>
                            </tr>
                        </tfoot> */}
                    </table>
                </div>

                {/* Summary Card - Modern Grid */}
                <div className="mb-5 rfq-summary-card" style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px', overflow: 'hidden' }}>
                    <div className="px-4 py-3 border-bottom rfq-summary-card-header" style={{ backgroundColor: '#f9fafb' }}>
                        <h6 className="mb-0" style={{ fontWeight: '700', color: '#374151', fontSize: '0.9rem' }}>Quotation Summary</h6>
                    </div>
                    <div className="p-4 rfq-summary-card-body">
                        <Row className="g-4">
                            <Col xl={6} md={6} xs={12} className="rfq-summary-stat">
                                <div className="d-flex align-items-start mb-1 text-muted small">
                                    <i className="bi bi-hash"></i> Quotation ID
                                </div>
                                <div style={{ fontWeight: '700', color: '#111827', fontSize: '1rem' }}>{bidInfo?.parts_rfq_bids_id || '-'}</div>
                            </Col>
                            <Col xl={6} md={6} xs={12}   className="rfq-summary-stat">
                                <div className="d-flex align-items-start mb-1 text-muted small">
                                    <i className="bi bi-truck"></i> Delivery
                                </div>
                                <div style={{ fontWeight: '700', color: '#111827', fontSize: '1rem', textTransform: 'capitalize' }}>{bidInfo?.delivery_type || '-'}</div>
                            </Col>
                            <Col xl={6} md={6} xs={12}  className="rfq-summary-stat">
                                <div className="d-flex align-items-start mb-1 text-muted small">
                                    <i className="bi bi-calendar3"></i> Expected Date
                                </div>
                                <div style={{ fontWeight: '700', color: '#111827', fontSize: '1rem' }}>
                                    {bidInfo?.delivery_date ? new Date(bidInfo.delivery_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                </div>
                            </Col>
                            <Col xl={6} md={6} xs={12}  className="rfq-summary-stat">
                                <div className="d-flex align-items-start mb-1 text-muted small">
                                    <i className="bi bi-wallet2"></i> Total Value
                                </div>
                                <div style={{ fontWeight: '800', color: '#1447B1', fontSize: '1.1rem' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '500', marginRight: '4px' }}>PKR</span>
                                    {formatNumber(summaryTotalAmount)}
                                </div>
                            </Col>
                        </Row>

                        <div className="mt-4 pt-4 border-top rfq-bid-breakdown">
                            <h6 style={{ fontWeight: '700', color: '#374151', fontSize: '0.9rem', marginBottom: '0.9rem' }}>
                                Cost Breakdown
                            </h6>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <span style={{ color: '#6b7280', fontSize: '0.87rem' }}>Subtotal</span>
                                <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.92rem' }}>PKR {formatNumber(summarySubTotal)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <span style={{ color: '#6b7280', fontSize: '0.87rem' }}>
                                    Discount ({formatPercent(summaryDiscountPercent)})
                                </span>
                                <span style={{ fontWeight: 700, color: '#dc3545', fontSize: '0.92rem' }}>
                                    - PKR {formatNumber(summaryDiscountAmount)}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <span style={{ color: '#6b7280', fontSize: '0.87rem' }}>Shipping</span>
                                <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.92rem' }}>
                                    + PKR {formatNumber(summaryShippingAmount)}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <span style={{ color: '#6b7280', fontSize: '0.87rem' }}>
                                    Tax ({formatPercent(summaryTaxPercent)})
                                </span>
                                <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.92rem' }}>
                                    + PKR {formatNumber(summaryTaxAmount)}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-start pt-2" style={{ borderTop: '1px solid #e5e7eb' }}>
                                <span style={{ fontWeight: 800, color: '#111827', fontSize: '0.94rem' }}>Total Amount</span>
                                <span style={{ fontWeight: 800, color: '#1447B1', fontSize: '0.98rem' }}>
                                    PKR {formatNumber(summaryTotalAmount)}
                                </span>
                            </div>
                        </div>
                        
                        {bidInfo?.notes && (
                            <div className="mt-4 pt-4 border-top">
                                <div className="d-flex align-items-center mb-2 text-muted small">
                                    <i className="bi bi-sticky me-2"></i> Vendor Notes
                                </div>
                                <p className="mb-0" style={{ fontSize: '0.875rem', color: '#4b5563', fontStyle: 'italic', backgroundColor: '#f9fafb', padding: '12px', borderRadius: '8px', borderLeft: '3px solid #1447B1' }}>
                                    "{bidInfo.notes}"
                                </p>
                            </div>
                        )}
                    </div>
                </div>

             

                <div className="text-center mt-5 mb-3 p-4 rfq-support-box" style={{ backgroundColor: '#f0f4ff', borderRadius: '12px', border: '1px dashed #c7d2fe' }}>
                    <h6 style={{ fontWeight: '700', color: '#1e40af', marginBottom: '8px' }}>Need help with this quotation?</h6>
                    <p style={{ color: '#3b82f6', fontSize: '0.85rem' }} className="mb-3">Our procurement support is available Mon-Fri, 9am - 6pm PKT.</p>
                    <div className="d-flex gap-2 justify-content-center rfq-support-actions">
                        <button 
                            className="btn btn-primary px-4 py-2 rfq-support-btn" 
                            onClick={() => window.location.href = 'mailto:enquiries@jholdings.com.pk'} 
                            style={{ borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', backgroundColor: '#1447B1', border: 'none' }}
                        >
                            <i className="bi bi-envelope me-2"></i> Email Support
                        </button>
                    </div>
                </div>
            </PageWrapper>
        )}
        </>
    
    );
};

export default RFQRequest;


